// ========================
// Core Entity Types
// ========================

export interface CrewMember {
  id?: number;
  crew_id?: string;
  cid: string;
  rank: string;
  name: string;
  surname?: string;
  nationality: string;
  gender?: string;
  birth_date?: string;
  date_of_birth?: string;
  sign_on_date?: string;
  vessel?: string;
  current_vessel?: string;
  due_date?: string;
  due_soff_date?: string;
  readiness?: string;
  next_vessel?: string;
  experience_months?: number;
  contracts?: number;
  vessel_type?: string;
  crew_type?: string;
  match_score?: number;
  match_reasons?: string[];
  availability_status?: string;
  experience_summary?: string;
  status?: string;
  fleet?: string;
  company?: string;
  movement_type?: string;
  crew_pool?: string;
  manning_office?: string;
  source_sheet?: string;
  // Contact fields
  contact_email?: string;
  contact_phone?: string;
  emergency_contact?: string;
  notes?: string;
  // Availability fields
  last_vessel?: string;
  last_sign_off?: string;
  available_from?: string;
}

export interface Vessel {
  id: number;
  name: string;
  teu_capacity?: number;
  category?: string;
  fleet?: string;
  company?: string;
  created_at?: string;
  updated_at?: string;
}

export interface VesselCategory {
  name: string;
  value?: string;
  code?: string;
  min_teu: number;
  max_teu: number;
  label?: string;
  teu?: string;
}

export interface CrewExperience {
  id?: number;
  cid: string;
  rank?: string;
  vessel?: string;
  vessel_category?: string;
  sign_on_date?: string;
  sign_off_date?: string;
  months_in_rank?: number;
  contracts?: number;
  movement_type?: string;
  crew_pool?: string;
  manning_office?: string;
  duration_months?: number;
}

export interface ReliefPlan {
  id: number;
  vessel_name: string;
  rank: string;
  outgoing_cid?: string;
  outgoing_name?: string;
  incoming_cid?: string;
  incoming_name?: string;
  due_date?: string;
  relief_date?: string;
  status?: 'pending' | 'confirmed' | 'overdue' | 'cancelled';
  notes?: string;
  role?: 'outgoing' | 'incoming';
  VESSEL?: string;
  RANK?: string;
  DUEDATE?: string;
  RELIEF?: string;
}

// ========================
// Search Types
// ========================

export interface CrewSearchQuery {
  rank?: string;
  vessel_category?: string;
  nationality?: string;
  min_experience_months?: number;
  min_contracts?: number;
  availability_days?: number;
  free_text_query?: string;
  natural_language_query?: string;
}

export interface SearchResult {
  query?: CrewSearchQuery | string;
  parsed_params?: {
    rank?: string;
    vessel_category?: string;
    min_experience_months?: number;
    availability_days?: number;
    nationality?: string;
  };
  total_results: number;
  recommendations: CrewMember[];
  analysis?: string;
  // Additional data for rich display
  summary?: any;
  dashboard?: any;
  intent?: string;
  vessels?: any[];
  relief_plans?: any[];
}

export interface CrewSearchResult {
  query: CrewSearchQuery;
  results: CrewMember[];
  summary: string;
}

// ========================
// Dashboard & Analytics Types
// ========================

export interface DashboardStats {
  total_active: number;
  total_available: number;
  total_vessels: number;
  total_relief_plans: number;
  by_rank: {
    MSTR: number;
    COFF: number;
    CENG: number;
    '3ENG': number;
    '4ENG': number;
    [key: string]: number;
  };
}

export interface DashboardOverview {
  overview: {
    total_active_crew: number;
    total_available_crew: number;
    total_vessels: number;
    total_relief_plans: number;
  };
  relief_status: {
    pending: number;
    confirmed: number;
    overdue: number;
  };
  upcoming_due_dates: {
    next_7_days: number;
    next_14_days: number;
    next_30_days: number;
  };
  active_by_rank: Record<string, number>;
  available_by_rank: Record<string, number>;
}

export interface Alert {
  type: 'overdue' | 'upcoming_due' | 'unassigned' | 'info' | 'warning';
  severity: 'high' | 'medium' | 'low';
  title: string;
  message: string;
  entity_type?: 'crew' | 'vessel' | 'relief_plan';
  entity_id?: number | string;
  created_at: string;
}

export interface AnalyticsOverview {
  nationality_distribution: Array<{ nationality: string; count: number }>;
  vessel_category_distribution: Array<{ category: string; count: number }>;
  experience_distribution: Array<{ category: string; count: number }>;
}

export interface RankDistribution {
  rank: string;
  active: number;
  available: number;
  total: number;
}

export interface NationalityDistribution {
  nationality: string;
  count: number;
}

export interface VesselCoverage {
  total_vessels: number;
  vessels_with_active_crew: number;
  vessels_with_relief_plans: number;
  coverage_percentage: number;
}

// ========================
// Workflow & Assignment Types
// ========================

export interface Assignment {
  id?: number;
  crew_cid: string;
  crew_name?: string;
  vessel_id: number;
  vessel_name: string;
  rank: string;
  sign_on_date?: string;
  expected_duration_months?: number;
  status?: 'pending' | 'active' | 'completed' | 'cancelled';
  notes?: string;
  created_at?: string;
}

export interface WorkflowStats {
  total_pending: number;
  total_confirmed: number;
  total_unassigned: number;
  completion_rate: number;
}

export interface AssignmentMatch {
  crew: CrewMember;
  match_score: number;
  match_reasons: string[];
}

// ========================
// Experience Matrix Types
// ========================

export interface ExperienceMatrixEntry {
  id?: number;
  cid: string;
  rank?: string;
  vessel?: string;
  vessel_category?: string;
  months_in_rank?: number;
  contracts?: number;
}

export interface ExperienceByCategory {
  vessel_category: string;
  rank: string;
  crew_count: number;
  avg_months: number;
}

// ========================
// Activity & Audit Types
// ========================

export interface ActivityLogEntry {
  type: string;
  entity_type: 'crew' | 'vessel' | 'relief_plan' | 'assignment';
  entity_id: number | string;
  description: string;
  timestamp: string;
  user?: string;
  changes?: Record<string, { old: any; new: any }>;
}

// ========================
// API Response Types
// ========================

export interface PaginatedResponse<T> {
  total: number;
  limit: number;
  offset: number;
  data: T[];
}

export interface CrewResponse {
  total: number;
  limit?: number;
  offset?: number;
  crew: CrewMember[];
}

export interface VesselResponse {
  total: number;
  limit?: number;
  offset?: number;
  vessels: Vessel[];
}

export interface ReliefPlanResponse {
  total: number;
  limit?: number;
  offset?: number;
  plans: ReliefPlan[];
}

export interface CrewDetailResponse {
  crew: CrewMember;
  experience: CrewExperience[];
  relief_history: ReliefPlan[];
  total_experience_months: number;
  total_contracts: number;
}

export interface VesselDetailResponse {
  vessel: Vessel;
  current_crew: CrewMember[];
  upcoming_relief_plans: ReliefPlan[];
}

// ========================
// Form & Filter Types
// ========================

export type SearchMode = 'ai' | 'filters';

export interface FilterState {
  rank: string;
  vesselCategory: string;
  nationality: string;
  minExperience: number;
  minContracts: number;
  availabilityDays: number;
  vessel: string;
  status: string;
}

export interface VesselFormData {
  name: string;
  teu_capacity?: number;
  category?: string;
  fleet?: string;
  company?: string;
}

export interface CrewFormData {
  // Required for creation
  cid?: string;
  name?: string;
  rank?: string;
  
  // Optional fields
  surname?: string;
  nationality?: string;
  status?: 'active' | 'available';
  date_of_birth?: string;
  contact_email?: string;
  contact_phone?: string;
  emergency_contact?: string;
  notes?: string;
  
  // For active crew
  vessel?: string;
  current_vessel?: string;
  sign_on_date?: string;
  due_date?: string;
  
  // For available crew
  last_vessel?: string;
  next_vessel?: string;
  last_sign_off?: string;
  available_from?: string;
  readiness?: string;
  crew_type?: string;
}

// ========================
// Vessel Position Suggestion Types
// ========================

export interface VesselPositionSuggestion {
  rank: string;
  rank_name: string;
  required: boolean;
  priority: 'critical' | 'high' | 'medium' | 'low';
  description: string;
  min_experience_months: number;
  suggested_count: number;
  available_crew_count: number;
  reason: string;
  // UI state (not from API)
  accepted?: boolean;
  modified?: boolean;
}

export interface VesselPositionSuggestionsResponse {
  vessel_category: string;
  suggestions: VesselPositionSuggestion[];
  total_positions: number;
  total_crew_needed: number;
  critical_positions: number;
}

export interface VesselCreateWithPositions {
  name: string;
  teu_capacity?: number;
  category?: string;
  fleet?: string;
  company?: string;
  positions?: VesselPositionSuggestion[];
}

export interface VesselCreateWithPositionsResponse {
  success: boolean;
  vessel: Vessel;
  positions_created: number;
  positions: Array<{
    rank: string;
    rank_name: string;
    status: string;
    priority: string;
  }>;
  message: string;
}

export interface ReliefPlanFormData {
  vessel_name: string;
  rank: string;
  outgoing_cid?: string;
  outgoing_name?: string;
  incoming_cid?: string;
  incoming_name?: string;
  due_date: string;
  status?: string;
  notes?: string;
}

// ========================
// Relief Plan AI Types
// ========================

export interface ReliefCrewSuggestion {
  cid: string;
  name: string;
  surname?: string;
  rank: string;
  nationality?: string;
  experience_months?: number;
  contracts?: number;
  crew_type?: string;
  readiness?: string;
  match_score: number;
  match_reasons: string[];
  due_date?: string;
}

export interface ReliefSuggestionResponse {
  plan_id: number;
  rank: string;
  vessel_name: string;
  due_date?: string;
  suggestions: ReliefCrewSuggestion[];
  ai_reasoning: string;
}

export interface ReliefStatistics {
  summary: {
    total: number;
    pending: number;
    confirmed: number;
    overdue: number;
    unassigned: number;
    completion_rate: number;
  };
  upcoming: {
    due_this_week: number;
    due_this_month: number;
  };
  by_rank: Array<{ rank: string; count: number }>;
  by_vessel: Array<{ vessel: string; count: number }>;
  alerts: {
    critical: number;
    warning: number;
    info: number;
  };
}

export interface BulkSuggestionResult {
  plan_id: number;
  rank: string;
  vessel_name: string;
  due_date?: string;
  top_suggestion?: ReliefCrewSuggestion;
  total_candidates: number;
}

export interface AutoAssignResponse {
  success: boolean;
  plan: ReliefPlan;
  assigned_crew: ReliefCrewSuggestion;
  ai_reasoning: string;
}

// ========================
// Enhanced AI Relief Planning Types
// ========================

export interface AIBulkSuggestion {
  plan_id: number;
  vessel_name: string;
  rank: string;
  due_date?: string;
  outgoing_name?: string;
  suggested_crew?: {
    cid: string;
    name: string;
    nationality?: string;
    match_score: number;
    match_reasons: string[];
  };
  alternatives_count: number;
  auto_assignable: boolean;
  warning?: string;
}

export interface AIBulkSuggestResponse {
  analysis_period: string;
  total_unassigned: number;
  suggestions_generated: number;
  auto_assignable_count: number;
  auto_assign_threshold: number;
  suggestions: AIBulkSuggestion[];
  auto_assignable: AIBulkSuggestion[];
  ai_summary: string;
}

export interface AIBulkExecuteResponse {
  total_processed: number;
  successful: number;
  failed: number;
  skipped: number;
  assignments: Array<{
    plan_id: number;
    vessel_name: string;
    rank: string;
    assigned_crew: {
      cid: string;
      name: string;
      match_score: number;
    };
  }>;
  failures: Array<{ plan_id: number; error: string }>;
  skipped_details: Array<{ plan_id: number; reason: string }>;
  ai_summary: string;
}

export interface ReliefScheduleEntry {
  crew_cid: string;
  crew_name: string;
  rank: string;
  vessel?: string;
  due_date?: string;
  days_until_due?: number;
  has_relief_plan: boolean;
  relief_plan_id?: number;
  relief_plan_status?: string;
  replacement_assigned: boolean;
  available_candidates: number;
  top_candidate?: {
    cid: string;
    name: string;
    nationality?: string;
  };
  urgency: 'critical' | 'high' | 'medium' | 'low';
}

export interface AIReliefScheduleResponse {
  period: string;
  total_crew_due: number;
  summary: {
    critical: number;
    high: number;
    medium: number;
    low: number;
    without_relief_plan: number;
    without_replacement: number;
  };
  schedule: ReliefScheduleEntry[];
  ai_recommendations: (string | null)[];
}

export interface AICreateFromScheduleResponse {
  success: boolean;
  plan?: ReliefPlan;
  suggested_replacement?: {
    cid: string;
    name: string;
    nationality?: string;
    match_score: number;
    match_reasons: string[];
  };
  message?: string;
  error?: string;
}

export interface AIOptimizeTimingResponse {
  plan_id: number;
  vessel_name: string;
  rank: string;
  current_due_date?: string;
  days_until_due?: number;
  timing_options: Array<{
    crew_cid: string;
    crew_name: string;
    earliest_available: string;
    days_until_available: number;
    nationality?: string;
    match_score: number;
  }>;
  recommendations: string[];
  optimal_timing?: {
    crew_cid: string;
    crew_name: string;
    earliest_available: string;
    days_until_available: number;
    nationality?: string;
    match_score: number;
  };
}

// ========================
// AI Auto-Generate Relief Plans
// ========================

export interface AIAutoGenerateRequest {
  days_ahead: number;
  include_existing?: boolean;
}

export interface AIAutoGenerateResponse {
  success: boolean;
  created_count: number;
  existing_count: number;
  skipped_count: number;
  created_plans: Array<{
    plan_id: number;
    vessel: string;
    rank: string;
    outgoing_crew: string;
    due_date?: string;
    suggested_replacement?: {
      cid: string;
      name: string;
      nationality?: string;
      match_score: number;
    };
  }>;
  skipped_plans: Array<{
    cid: string;
    name: string;
    vessel: string;
    rank: string;
    reason: string;
  }>;
  ai_summary: string;
}

// ========================
// AI Conflict Detection
// ========================

export interface AIConflict {
  type: string;
  severity: 'critical' | 'warning';
  message: string;
  plan_id?: number;
  vessel?: string;
  rank?: string;
  due_date?: string;
  days_until_due?: number;
  crew_cid?: string;
  crew_name?: string;
  count?: number;
  ranks?: string[];
  plan_ids?: number[];
  assignments?: Array<{
    plan_id: number;
    vessel: string;
    rank: string;
    due_date?: string;
  }>;
}

export interface AIConflictResponse {
  total_plans_checked: number;
  conflicts: AIConflict[];
  warnings: AIConflict[];
  conflict_count: number;
  warning_count: number;
  ai_summary: string;
  is_healthy: boolean;
}

// ========================
// Relief Plan Analytics
// ========================

export interface ReliefPlanAnalytics {
  period: {
    start: string;
    end: string;
    days: number;
  };
  overview: {
    total_plans: number;
    confirmed_plans: number;
    completion_rate: number;
    avg_days_to_assignment: number;
  };
  monthly_trend: Array<{
    month?: string;
    total: number;
    confirmed: number;
  }>;
  by_rank: Array<{
    rank: string;
    total: number;
    assignment_rate: number;
  }>;
  top_vessels: Array<{
    vessel: string;
    count: number;
  }>;
  ai_insights: string[];
}

// ========================
// Crew Search for Relief
// ========================

export interface CrewSearchResult {
  cid: string;
  name: string;
  surname?: string;
  rank: string;
  nationality?: string;
  due_date?: string;
  readiness?: string;
  crew_type?: string;
  match_score: number;
  match_reasons: string[];
}

export interface CrewSearchResponse {
  total: number;
  crew: CrewSearchResult[];
}

// ========================
// Reference Data Types
// ========================

export interface Rank {
  code: string;
  name: string;
  value: string;
  label: string;
  department?: 'Deck' | 'Engine';
}

export interface StatusOption {
  value: string;
  label: string;
  color: string;
}

// ========================
// Constants
// ========================

export const RANKS: Rank[] = [
  { code: 'MSTR', name: 'Master', value: 'MSTR', label: 'Master', department: 'Deck' },
  { code: 'COFF', name: 'Chief Officer', value: 'COFF', label: 'Chief Officer', department: 'Deck' },
  { code: '2OFF', name: 'Second Officer', value: '2OFF', label: 'Second Officer', department: 'Deck' },
  { code: '3OFF', name: 'Third Officer', value: '3OFF', label: 'Third Officer', department: 'Deck' },
  { code: 'CENG', name: 'Chief Engineer', value: 'CENG', label: 'Chief Engineer', department: 'Engine' },
  { code: '2ENG', name: 'Second Engineer', value: '2ENG', label: 'Second Engineer', department: 'Engine' },
  { code: '3ENG', name: 'Third Engineer', value: '3ENG', label: '3rd Engineer', department: 'Engine' },
  { code: '4ENG', name: 'Fourth Engineer', value: '4ENG', label: '4th Engineer', department: 'Engine' },
];

export const VESSEL_CATEGORIES: VesselCategory[] = [
  { value: 'Very Large', code: 'VLV', label: 'Very Large Vessels', name: 'Very Large Vessels', teu: '13,000 - 20,000 TEU', min_teu: 13000, max_teu: 20000 },
  { value: 'Large', code: 'LV', label: 'Large Vessels', name: 'Large Vessels', teu: '8,000 - 12,999 TEU', min_teu: 8000, max_teu: 12999 },
  { value: 'Medium', code: 'MV', label: 'Medium Vessels', name: 'Medium Vessels', teu: '3,501 - 7,999 TEU', min_teu: 3501, max_teu: 7999 },
  { value: 'Small', code: 'SV', label: 'Small Vessels', name: 'Small Vessels', teu: '100 - 3,500 TEU', min_teu: 100, max_teu: 3500 },
];

export const STATUS_OPTIONS: StatusOption[] = [
  { value: 'onboard', label: 'Onboard', color: 'green' },
  { value: 'available', label: 'Available', color: 'blue' },
  { value: 'on_leave', label: 'On Leave', color: 'yellow' },
  { value: 'training', label: 'Training', color: 'purple' },
  { value: 'medical', label: 'Medical Leave', color: 'red' },
  { value: 'pending_assignment', label: 'Pending Assignment', color: 'orange' },
];

export const RELIEF_STATUS_OPTIONS: StatusOption[] = [
  { value: 'pending', label: 'Pending', color: 'yellow' },
  { value: 'confirmed', label: 'Confirmed', color: 'green' },
  { value: 'overdue', label: 'Overdue', color: 'red' },
  { value: 'cancelled', label: 'Cancelled', color: 'gray' },
];

// ========================
// Vessel Crew Planning Types
// ========================

export interface CrewSuggestion {
  cid: string;
  name: string;
  nationality?: string;
  due_date?: string;
  vessel_type?: string;
  match_score: number;
  match_reasons?: string[];
}

export interface PositionSuggestion {
  position: string;
  position_name: string;
  status: 'open' | 'filled' | 'suggested' | 'accepted' | 'rejected' | 'changed';
  current_crew?: CrewSuggestion | null;
  suggested_crew?: CrewSuggestion | null;
  selected_crew?: CrewSuggestion | null;
  alternatives: CrewSuggestion[];
}

export interface VesselCrewPlan {
  vessel_id: number;
  vessel_name: string;
  vessel_category?: string;
  positions: PositionSuggestion[];
  total_positions: number;
  filled_positions: number;
  suggested_positions: number;
}

export interface VesselCrewPlanConfirmation {
  vessel_id: number;
  vessel_name: string;
  assignments: Array<{
    position: string;
    crew_cid: string;
    crew_name: string;
  }>;
}

// CrewSuggestion is already defined as an interface above

export interface VesselCrewStatus {
  vessel_id: number;
  vessel_name: string;
  vessel_category?: string;
  total_positions: number;
  filled_count: number;
  open_count: number;
  filled_positions: Record<string, Array<{
    cid: string;
    name: string;
    nationality?: string;
    sign_on_date?: string;
    due_date?: string;
  }>>;
  open_positions: string[];
}

// ========================
// Utility Types
// ========================

export type SortDirection = 'asc' | 'desc';

export interface SortConfig {
  field: string;
  direction: SortDirection;
}

export interface TableColumn<T> {
  key: keyof T | string;
  label: string;
  sortable?: boolean;
  width?: string;
  render?: (item: T) => React.ReactNode;
}

// ========================
// Component Props Types
// ========================

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

export interface TableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  onRowClick?: (item: T) => void;
  isLoading?: boolean;
  emptyMessage?: string;
}

export interface FormProps<T> {
  initialData?: T;
  onSubmit: (data: T) => void;
  onCancel: () => void;
  isLoading?: boolean;
}
